import React from 'react';
import { Input } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';

export interface PriceSummaryTableRowProps {
    label: React.ReactNode;
    indented?: boolean;

    faceValue?: number | null;
    onFaceValueChange?: (v: number | null) => void;
    faceValueAriaLabel: string;
    faceValueErrorKey?: string;
    allowNegativeFaceValue?: boolean;
    percent?: string | null;

    showSellerEmployeeCol: boolean;
    considerationForEmployees?: number | null;
    onConsiderationChange?: (v: number | null) => void;
    considerationAriaLabel?: string;
    considerationErrorKey?: string;

    showBuyerEmployeeCol: boolean;
    buyerConsiderationForEmployees?: number | null;
    onBuyerConsiderationChange?: (v: number | null) => void;
    buyerConsiderationAriaLabel?: string;
    buyerConsiderationErrorKey?: string;

    showFairValueCols: boolean;
    fairValue?: number | null;
    onFairValueChange?: (v: number | null) => void;
    fairValueAriaLabel?: string;
    fairValueErrorKey?: string;
    explanation?: string | null;
    onExplanationChange?: (v: string | null) => void;
    explanationAriaLabel?: string;

    showAdminComment: boolean;
    adminCommentNode?: React.ReactNode;

    showComments: boolean;
    commentNode?: React.ReactNode;

    readOnly?: boolean;
    errors?: Set<string>;

    isHeaderOnly?: boolean;

    canEditFairValues?: boolean;
}

export function parseCurrencyValue(value: string | undefined): number | null {
    return value === undefined || value === '' || value === 'NaN' ? null : Number(value);
}

export const PriceSummaryTableRow: React.FC<PriceSummaryTableRowProps> = ({
    label,
    indented,
    faceValue,
    onFaceValueChange,
    faceValueAriaLabel,
    faceValueErrorKey,
    allowNegativeFaceValue,
    percent,
    showSellerEmployeeCol,
    considerationForEmployees,
    onConsiderationChange,
    considerationAriaLabel,
    considerationErrorKey,
    showBuyerEmployeeCol,
    buyerConsiderationForEmployees,
    onBuyerConsiderationChange,
    buyerConsiderationAriaLabel,
    buyerConsiderationErrorKey,
    showFairValueCols,
    fairValue,
    onFairValueChange,
    fairValueAriaLabel,
    fairValueErrorKey,
    explanation,
    onExplanationChange,
    explanationAriaLabel,
    showAdminComment,
    adminCommentNode,
    showComments,
    commentNode,
    readOnly,
    errors,
    isHeaderOnly,
    canEditFairValues,
}) => {
    const fairValuesLocked = readOnly && !canEditFairValues;

    return isHeaderOnly ? (
    <tr className="terms-form-price-summary-header-row">
        <td className={`terms-form-col-item table-label-cell${indented ? ' terms-form-indented' : ''}`}>
            {label}
        </td>
        <td className="terms-form-col-value" />
        <td className="terms-form-col-percent" />
        {showSellerEmployeeCol && <td className="terms-form-col-value" />}
        {showBuyerEmployeeCol && <td className="terms-form-col-value" />}
        {showFairValueCols && (
            <>
                <td className="terms-form-col-value" />
                <td className="terms-form-col-explanation" />
            </>
        )}
        {showAdminComment && <td className="terms-form-col-comment terms-form-col-admin-comment" />}
        {showComments && <td className="terms-form-col-comment" />}
    </tr>
) : (
    <tr>
        <td className={`terms-form-col-item table-label-cell${indented ? ' terms-form-indented' : ''}`}>
            {label}
        </td>
        <td className="terms-form-col-value">
            <CurrencyInput
                value={faceValue != null ? String(faceValue) : ''}
                allowDecimals
                allowNegativeValue={allowNegativeFaceValue ?? false}
                prefix="$"
                groupSeparator=","
                decimalSeparator="."
                disabled={readOnly || !onFaceValueChange}
                onValueChange={onFaceValueChange ? (v) => onFaceValueChange(parseCurrencyValue(v)) : undefined}
                className={`form-control loi-form-input${faceValueErrorKey && errors?.has(faceValueErrorKey) ? ' is-invalid' : ''}`}
                aria-label={faceValueAriaLabel}
            />
        </td>
        <td className={`terms-form-col-percent table-label-cell${percent ? ' terms-form-percent-label' : ''}`}>
            {percent ?? ''}
        </td>
        {showSellerEmployeeCol && (
            <td className="terms-form-col-value">
                {onConsiderationChange && (
                    <CurrencyInput
                        value={considerationForEmployees != null ? String(considerationForEmployees) : ''}
                        allowDecimals
                        allowNegativeValue={false}
                        prefix="$"
                        groupSeparator=","
                        decimalSeparator="."
                        disabled={readOnly}
                        onValueChange={(v) => onConsiderationChange(parseCurrencyValue(v))}
                        className={`form-control loi-form-input${considerationErrorKey && errors?.has(considerationErrorKey) ? ' is-invalid' : ''}`}
                        aria-label={considerationAriaLabel ?? ''}
                    />
                )}
            </td>
        )}
        {showBuyerEmployeeCol && (
            <td className="terms-form-col-value">
                {onBuyerConsiderationChange && (
                    <CurrencyInput
                        value={buyerConsiderationForEmployees != null ? String(buyerConsiderationForEmployees) : ''}
                        allowDecimals
                        allowNegativeValue={false}
                        prefix="$"
                        groupSeparator=","
                        decimalSeparator="."
                        disabled={readOnly}
                        onValueChange={(v) => onBuyerConsiderationChange(parseCurrencyValue(v))}
                        className={`form-control loi-form-input${buyerConsiderationErrorKey && errors?.has(buyerConsiderationErrorKey) ? ' is-invalid' : ''}`}
                        aria-label={buyerConsiderationAriaLabel ?? ''}
                    />
                )}
            </td>
        )}
        {showFairValueCols && (
            <>
                <td className="terms-form-col-value">
                    {onFairValueChange && (
                        <CurrencyInput
                            value={fairValue != null ? String(fairValue) : ''}
                            allowDecimals
                            allowNegativeValue={false}
                            prefix="$"
                            groupSeparator=","
                            decimalSeparator="."
                            disabled={fairValuesLocked}
                            onValueChange={(v) => onFairValueChange(parseCurrencyValue(v))}
                            className={`form-control loi-form-input${fairValueErrorKey && errors?.has(fairValueErrorKey) ? ' is-invalid' : ''}`}
                            aria-label={fairValueAriaLabel ?? ''}
                        />
                    )}
                </td>
                <td className="terms-form-col-explanation">
                    {onExplanationChange && (
                        <Input
                            type="text"
                            value={explanation ?? ''}
                            disabled={fairValuesLocked}
                            onChange={(e) => onExplanationChange(e.target.value === '' ? null : e.target.value)}
                            className="form-control loi-form-input"
                            aria-label={explanationAriaLabel ?? ''}
                        />
                    )}
                </td>
            </>
        )}
        {showAdminComment && (
            <td className="terms-form-col-comment terms-form-col-admin-comment">
                {adminCommentNode}
            </td>
        )}
        {showComments && (
            <td className="terms-form-col-comment">
                {commentNode}
            </td>
        )}
    </tr>
    );
};
